import { get, isEmpty, last } from 'lodash-es';
import mapValues from 'lodash-es/mapValues';
import { createAction } from 'redux-actions';

import { normalizeListing } from '@ha/algolia';
import reportError from 'ha/helpers/bugsnag/reportError';
import getTranslatedCollection from 'ha/utils/urls/getTranslatedCollection';
import { getFeatureFlags } from 'ha/modules/FeatureFlags';
import { DEFAULT_CURRENCY } from 'ha/constants/Currencies';
import HttpStatus from 'ha/constants/HttpStatus';

import { getSearchRequestId, getCurrentIndex } from '../selectors';
import { getFilterBuilderContext } from '../selectors/algoliaSelectors';
import {
  getAlgoliaListingsQuery,
  getAlgoliaFacetQuery,
  reformatPriceDistribution,
  getPriceDistribution,
  getTrimmedPriceDistribution,
  normalizeMinMaxPrices,
  getFiltersCountFromFacetQuery,
} from '../utils';
import { Actions, FacetQueryAttributes } from '../constants';
import updateSearch from './updateSearch';
import loadCurrencyRates from './loadCurrencyRates';
import { getNoFilterFacet } from '../selectors/filterSelectors';

export const flow = {
  start: createAction(Actions.LOAD_START),
  done: createAction(Actions.LOAD_DONE),
  error: createAction(Actions.LOAD_ERROR),
  notFound: createAction(Actions.LOAD_NOT_FOUND),
  unsupportedCountry: createAction(Actions.LOAD_UNSUPPORTED_COUNTRY),
};

export default (reqParams, { localizedKind, priceAverage }) =>
  (dispatch, getState, services) => {
    const requestId = Date.now();

    dispatch(flow.start(requestId));

    return Promise.all([
      dispatch(updateSearch()),
      dispatch(loadCurrencyRates()),
    ])
      .then(
        ([
          searchParams,
          {
            data: { rates },
          },
        ]) => {
          const state = getState();
          const indexName = getCurrentIndex(state);
          const { withDynamicMinimumPrice } = getFeatureFlags(state);
          const noFiltersFacet = getNoFilterFacet(state);

          return Promise.all([
            Promise.resolve(searchParams),
            Promise.resolve(rates),
            services.algolia
              .searchListings(
                [
                  getAlgoliaListingsQuery(
                    reqParams,
                    searchParams,
                    rates,
                    getFilterBuilderContext(state, 'listings'),
                  ),
                  getAlgoliaFacetQuery(
                    withDynamicMinimumPrice === 'on'
                      ? ['minPrice']
                      : ['priceEUR'],
                    reqParams,
                    searchParams,
                    rates,
                    getFilterBuilderContext(state, 'priceFacet'),
                  ),
                  getAlgoliaFacetQuery(
                    ['propertyType', 'apartmentBedroomCount'],
                    reqParams,
                    searchParams,
                    rates,
                    getFilterBuilderContext(state, 'propertyTypeFacet'),
                  ),
                  getAlgoliaFacetQuery(
                    ['propertySize'],
                    reqParams,
                    searchParams,
                    rates,
                    getFilterBuilderContext(state, 'propertySizeFacet'),
                  ),
                  ...(noFiltersFacet
                    ? []
                    : [
                        getAlgoliaFacetQuery(
                          FacetQueryAttributes,
                          {},
                          searchParams,
                          rates,
                          getFilterBuilderContext(state, 'listings'),
                        ),
                      ]),
                ],
                indexName,
              )
              .then(response => response.results),
          ]);
        },
      )
      .then(([searchParams, currencyEuroRates, algoliaResponses]) => {
        /* eslint-disable prefer-const */
        let [
          listings,
          priceFacet,
          propertyTypeFacet,
          propertySizeFacet,
          noFiltersFacet = {},
        ] = algoliaResponses;
        /* eslint-enable prefer-const */

        const state = getState();
        const { withDynamicMinimumPrice } = getFeatureFlags(state);

        noFiltersFacet = isEmpty(noFiltersFacet)
          ? getNoFilterFacet(state)
          : noFiltersFacet;

        if (getSearchRequestId(state) !== requestId) {
          // Query is not the last one. Dismiss results.
          return;
        }

        // in the first load use either the user specified currency (from query params)
        //  or default to the country currency from /api/v2/geonames/search-city call.
        const currency =
          reqParams.currency || searchParams.currency || DEFAULT_CURRENCY;
        const currencyRate = currencyEuroRates[currency];
        const currencyRates = currencyEuroRates;

        let min = get(priceFacet.facets_stats, ['priceEUR', 'min'], 0);
        let max = get(priceFacet.facets_stats, ['priceEUR', 'max'], 0);

        if (withDynamicMinimumPrice === 'on') {
          min = get(priceFacet.facets_stats, ['minPrice', 'min'], 0);
          max = get(priceFacet.facets_stats, ['minPrice', 'max'], 0);
        }

        const { minPrice, maxPrice } = normalizeMinMaxPrices(
          min,
          max,
          currencyRate,
        );

        const distributionArg = {
          pricesFacet:
            (withDynamicMinimumPrice === 'on'
              ? priceFacet.facets?.minPrice
              : priceFacet.facets?.priceEUR) || [],
          minPrice,
          maxPrice,
          rate: currencyRate,
        };

        const priceDistribution = reformatPriceDistribution(
          getPriceDistribution(distributionArg),
        );
        const priceMin = minPrice * 100;
        const priceMax = maxPrice * 100;

        const trimmedPriceDistribution = reformatPriceDistribution(
          getTrimmedPriceDistribution(distributionArg),
        );
        const trimmedPriceMin = priceMin;
        const trimmedPriceMax = last(trimmedPriceDistribution).range * 100;

        const langURLListWithCollections = mapValues(
          searchParams.langURLList,
          (url, lang) => {
            const translatedCollection = getTranslatedCollection(
              lang,
              localizedKind,
            );

            return `${url}${
              translatedCollection ? `/${translatedCollection}` : ''
            }`;
          },
        );

        const propertySizeMin =
          propertySizeFacet?.facets_stats?.propertySize?.min || 0;
        const propertySizeMax =
          propertySizeFacet?.facets_stats?.propertySize?.max || 0;

        dispatch(
          flow.done({
            ...searchParams,
            langURLListWithCollections,
            currency,
            defaultCurrency: searchParams.currency,
            edges: listings.hits.map(
              normalizeListing({
                currency,
                currencyRate,
              }),
            ),
            pageInfo: {
              offset: listings.page,
              total: listings.nbHits,
              pages: listings.nbPages,
              limit: listings.hitsPerPage,
              isExhaustive: Boolean(listings.exhaustiveNbHits),
            },
            listingsCount: listings.nbHits,
            currencyRates,
            priceMin,
            priceMax,
            priceAverage,
            priceDistribution,
            trimmedPriceMin,
            trimmedPriceMax,
            trimmedPriceDistribution,
            listingsSummary: {
              organic: get(listings, ['facets', 'isPartner', 'false'], 0),
              partner: get(listings, ['facets', 'isPartner', 'true'], 0),
              isExhaustive: get(listings, ['exhaustiveFacetsCount'], true),
            },
            filtersCount: getFiltersCountFromFacetQuery(
              listings,
              propertyTypeFacet,
              propertySizeFacet,
            ),
            noFiltersFacet,
            globalFiltersCount: getFiltersCountFromFacetQuery(
              noFiltersFacet,
              {},
              {},
            ),
            propertySizeMin,
            propertySizeMax,
          }),
        );
      })
      .catch(error => {
        if (error.status === HttpStatus.NOT_FOUND) {
          dispatch(flow.notFound());
          return;
        }
        if (error.status === HttpStatus.GONE) {
          dispatch(flow.unsupportedCountry());
          return;
        }
        reportError('Load search results failed', { metaData: { error } });

        dispatch(flow.error());
      });
  };
